import { Direction, Movement } from '@studio/models/ptz';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { Choreographer } from './choreographer.jsx';
import { Diagonal } from './diagonal.jsx';
import { Orthogonal } from './orthogonal.jsx';
import type { Props } from './types.js';
import { Zoom } from './zoom.jsx';
import { DataSTag, Sel } from '#~/stag/tag.js';

/** `<DPad>` component. */
export const DPad = ({
  allowZoom,
  canPan,
  canTilt,
  className,
  disabled,
  ...props
}: Props): ReactElement => (
  // Per playwright-stag-selectors-only (ai.knowledge/platform): the DPad
  // wrapper gets a data stag so IR-039's mouseleave test can read the
  // Choreographer wrapper's bounding box without falling back to
  // xpath=.. parent traversal. Choreographer spreads `...props` onto
  // its wrapper div (choreographer.tsx:84), so the stag lands on the
  // correct element.
  <Choreographer
    className={twMerge(
      'relative aspect-square transition-opacity',
      disabled && 'pointer-events-none opacity-disabled',
      className
    )}
    canPan={canPan}
    canTilt={canTilt}
    canZoom={!!allowZoom}
    {...props}
    {...DataSTag(Sel.MainRoomRightTrayControls, 'dpad-wrapper')}
  >
    {/* North */}
    <Orthogonal
      className='left-1/3'
      direction={Direction.North}
      disabled={!!disabled || !canTilt}
    />

    {/* Northeast */}
    <Diagonal
      className='right-0 rotate-90'
      direction={Direction.Northeast}
      disabled={!!disabled || !canTilt || !canPan}
    />

    {/* East */}
    <Orthogonal
      className='right-0 top-1/3 rotate-90'
      direction={Direction.East}
      disabled={!!disabled || !canPan}
    />

    {/* Southeast */}
    <Diagonal
      className='bottom-0 right-0 rotate-180'
      direction={Direction.Southeast}
      disabled={!!disabled || !canTilt || !canPan}
    />

    {/* South */}
    <Orthogonal
      className='bottom-0 left-1/3 rotate-180'
      direction={Direction.South}
      disabled={!!disabled || !canTilt}
    />

    {/* Southwest */}
    <Diagonal
      className='bottom-0 -rotate-90'
      direction={Direction.Southwest}
      disabled={!!disabled || !canTilt || !canPan}
    />

    {/* West */}
    <Orthogonal
      className='top-1/3 -rotate-90'
      direction={Direction.West}
      disabled={!!disabled || !canPan}
    />

    {/* Northwest */}
    <Diagonal direction={Direction.Northwest} disabled={!!disabled || !canTilt || !canPan} />

    {!!allowZoom && (
      <>
        {/* Zoom In */}
        <Zoom className='left-1/3 top-1/3' movement={Movement.ZoomIn} />

        {/* Zoom Out */}
        <Zoom className='bottom-1/3 left-1/3 rotate-180' movement={Movement.ZoomOut} />
      </>
    )}
  </Choreographer>
);
